import React, {useEffect} from 'react';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import {getToken} from "../../ajax/ajax";
import PhotoSwipeFullscreen from './photoswipe-fullscreen';
import PhotoSwipeSlideShow from './photoswipe-slideshow';
import PhotoSwipeShowHideControls from './photoswipe-show-hide-controls';
import 'photoswipe/style.css';
import './GalleryImages.scss';
import {v4} from "uuid";
import PhotoSwipeCaptions from "./photoswipe-captions";
import isTouchDevice from "../../util/isTouchDevice";
import formatDuration from "../../util/formatDuration";

function withToken(url) {
  return url + '?token=' + encodeURIComponent(getToken());
}

const GalleryImages = ({images}) => {
  const containerId = 'c'+v4();
  useEffect(() => {
    const lightbox = new PhotoSwipeLightbox({
      gallery: '#'+containerId,
      children: 'a',
      thumbSelector: 'img',
      pswpModule: () => import('photoswipe')
    });

    // PhotoSwipe only picks up the handful of data-pswp-* attributes it knows about, so the
    // video source has to be copied across by hand.
    lightbox.addFilter('itemData', (itemData) => {
      const element = itemData.element;
      if (element && element.dataset.pswpVideoSrc) {
        itemData.videoSrc = element.dataset.pswpVideoSrc;
        itemData.videoDuration = parseFloat(element.dataset.pswpVideoDuration || '0');
      }

      return itemData;
    });

    // v5 has no built-in video support: a slide marked data-pswp-type="video" gets its
    // element built here instead of being loaded as an image.
    lightbox.on('contentLoad', (e) => {
      const {content} = e;
      if (content.type !== 'video') {
        return;
      }

      e.preventDefault();
      content.element = document.createElement('div');
      content.element.className = 'pswp__video-container';

      const video = document.createElement('video');
      video.src = content.data.videoSrc;
      video.poster = content.data.msrc;
      video.controls = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'metadata';
      content.element.appendChild(video);
    });

    lightbox.on('contentActivate', ({content}) => {
      if (content.type !== 'video' || !content.element) {
        return;
      }
      const video = content.element.querySelector('video');
      if (video) {
        // Opening the lightbox is a click, so the page normally has the user activation
        // browsers ask for before playing with sound.
        const playAttempt = video.play();
        if (playAttempt && playAttempt.catch) {
          playAttempt.catch(() => {
            // Sound was refused anyway; play silently rather than not at all.
            video.muted = true;
            const mutedAttempt = video.play();
            if (mutedAttempt && mutedAttempt.catch) {
              mutedAttempt.catch(() => {
                // Autoplay refused entirely; the poster and the controls are still there.
              });
            }
          });
        }
      }
    });

    // Without this a clip keeps playing in the background once it is swiped away.
    lightbox.on('contentDeactivate', ({content}) => {
      if (content.type !== 'video' || !content.element) {
        return;
      }
      const video = content.element.querySelector('video');
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    });

    new PhotoSwipeFullscreen(lightbox, {
      fullscreenTitle: 'Vollbild'
    });
    new PhotoSwipeSlideShow(lightbox, {});
    if (!isTouchDevice()) {
      new PhotoSwipeShowHideControls(lightbox, {});
    }
    new PhotoSwipeCaptions(lightbox, {});
    lightbox.init();

    return () => {
      lightbox.destroy();
    }

  }, [images]);

  return (
    <div className="GalleryImages" id={containerId}>
        {images.map((image) => {
          // A clip that is still encoding has no still and nothing to open yet, so it is
          // rendered as a plain div and PhotoSwipe skips over it.
          if (image.status !== 'ready') {
            return (
              <div key={image.id} className="GalleryImages__image">
                <div className="GalleryImages__pending">Video wird verarbeitet…</div>
              </div>
            );
          }

          const isVideo = image.mediaType === 'video';

          return (
            <div key={image.id} className="GalleryImages__image">
              <a
                className="GalleryImages__thumb"
                data-pswp-src={withToken(image.large)}
                data-pswp-width={image.width}
                data-pswp-height={image.height}
                data-pswp-type={isVideo ? 'video' : undefined}
                data-pswp-video-src={isVideo ? withToken(image.video) : undefined}
                data-pswp-video-duration={isVideo ? image.duration : undefined}
              >
                <img src={withToken(image.small)} alt={image.text} />
                {isVideo ? (
                  <span className="GalleryImages__play">
                    <svg viewBox="0 0 24 24" width="44" height="44" aria-hidden="true"><path fill="currentColor" d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM10 8l6 4-6 4V8z"/></svg>
                  </span>
                ) : null}
                {isVideo && image.duration ? (
                  <span className="GalleryImages__duration">{formatDuration(image.duration)}</span>
                ) : null}
              </a>
            </div>
            );
        })}
    </div>
  );
}

export default GalleryImages;
